'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { LandingData } from './CompatibilityReaderClient';

interface FormValues {
  name: string;
  email: string;
  password: string;
  disclaimer: boolean;
}

interface InputFormProps {
  onComplete: (data: LandingData) => void;
}

const FIXED_PASSWORD = 'shunsheng';

export default function InputForm({ onComplete }: InputFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const disclaimerChecked = watch('disclaimer');

  const onSubmit = (data: FormValues) => {
    onComplete({ name: data.name.trim(), email: data.email.trim() });
  };

  return (
    <div className="mb-0">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="gradient-border rounded-2xl p-1">
          <div className="bg-slate-900/90 rounded-xl p-6 lg:p-8">
            <div className="max-w-lg mx-auto space-y-5">

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Full Name
                </label>
                <input
                  {...register('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                  type="text"
                  placeholder="e.g. Alex Loh"
                  className="input-cosmic w-full rounded-xl px-4 py-3 text-slate-100 placeholder-slate-600 text-sm"
                />
                {errors.name && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Email Address
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  type="email"
                  placeholder="e.g. alex@example.com"
                  className="input-cosmic w-full rounded-xl px-4 py-3 text-slate-100 placeholder-slate-600 text-sm"
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.email.message}
                  </p>
                )}
              </div>

              {/* Access Password */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Access Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Access password is required',
                      validate: (value) =>
                        value === FIXED_PASSWORD || 'Incorrect password. Please contact the administrator.',
                    })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter access password"
                    className="input-cosmic w-full rounded-xl px-4 py-3 pr-12 text-slate-100 placeholder-slate-600 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors text-xs select-none"
                    tabIndex={-1}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.password.message}
                  </p>
                )}
              </div>

              {/* Disclaimer Checkbox */}
              <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      {...register('disclaimer', {
                        required: 'You must acknowledge the disclaimer before proceeding.',
                      })}
                      type="checkbox"
                      className="sr-only peer"
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${disclaimerChecked ? 'border-amber-400 bg-amber-400/20' : 'border-slate-500 group-hover:border-slate-400'}`}>
                      <svg
                        className={`w-3 h-3 text-amber-400 transition-opacity ${disclaimerChecked ? 'opacity-100' : 'opacity-0'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                    I understand that all results generated are indicative only and must be reviewed and confirmed by a qualified practitioner, and cross-referenced with the Behavioural Assessment before drawing any conclusion.
                  </span>
                </label>
                {errors.disclaimer && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                    <span>⚠</span> {errors.disclaimer.message}
                  </p>
                )}
              </div>

              {/* Submit */}
              <div className="pt-2 flex justify-center">
                <button
                  type="submit"
                  className="btn-primary w-full sm:w-auto px-10 py-3.5 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-3 min-w-[240px]"
                >
                  <span>☯</span>
                  <span>Begin Assessment</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </form>
    </div>
  );
}